import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../../config';
import './ApplicationQuestionsManager.css';

const DEFAULT_SECTIONS = [
    'Personal Identity',
    'Residential History',
    'Employment & Income',
    'Financial',
    'Background',
    'Tenancy Details',
    'Emergency Contact',
    'Signature & Authorization'
];

const FIELD_TYPES = ['text', 'email', 'tel', 'date', 'number', 'textarea', 'select', 'checkbox'];

const ApplicationQuestionsManager = () => {
    const [questions, setQuestions] = useState([]);
    const [sections, setSections] = useState({});
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filterSection, setFilterSection] = useState('all');
    const [customSection, setCustomSection] = useState('');

    useEffect(() => {
        loadQuestions();
    }, []);

    const loadQuestions = async () => {
        try {
            setLoading(true);
            // include_all=true so the admin sees every question, including disabled ones
            const response = await fetch(`${API_BASE_URL}/api/application/questions?include_all=true`);
            if (response.ok) {
                const data = await response.json();
                setQuestions(data.questions || []);
                setSections(data.sections || {});
            }
        } catch (err) {
            setError('Failed to load questions');
        } finally {
            setLoading(false);
        }
    };

    // Build a unique list of sections from existing data + defaults
    const allSections = useMemo(() => {
        const existing = Object.keys(sections);
        const merged = [...new Set([...DEFAULT_SECTIONS, ...existing])];
        return merged.sort();
    }, [sections]);

    const handleSaveQuestion = async (questionData) => {
        try {
            setError('');
            const url = questionData.id
                ? `${API_BASE_URL}/api/application/questions/${questionData.id}`
                : `${API_BASE_URL}/api/application/questions`;

            const method = questionData.id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(questionData)
            });

            if (response.ok) {
                setSuccess(questionData.id ? 'Question updated successfully' : 'Question created successfully');
                setEditingQuestion(null);
                setShowAddModal(false);
                setCustomSection('');
                loadQuestions();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to save question');
            }
        } catch (err) {
            setError('Failed to save question');
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!window.confirm('Are you sure you want to delete this question?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/application/questions/${questionId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                setSuccess('Question deleted successfully');
                loadQuestions();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError('Failed to delete question');
            }
        } catch (err) {
            setError('Failed to delete question');
        }
    };

    const handleToggleActive = async (question) => {
        const newActive = question.is_active === 0 ? 1 : 0;
        try {
            const response = await fetch(`${API_BASE_URL}/api/application/questions/${question.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: newActive })
            });
            if (response.ok) {
                setSuccess(`Question ${newActive ? 'activated' : 'deactivated'}`);
                loadQuestions();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError('Failed to toggle question');
        }
    };

    const openAdd = () => {
        setEditingQuestion({
            section: 'Personal Identity',
            question_text: '',
            field_name: '',
            field_type: 'text',
            required: 0,
            placeholder: '',
            options: null,
            order_index: 0,
            is_active: 1
        });
        setCustomSection('');
        setShowAddModal(true);
        setError('');
    };

    const openEdit = (question) => {
        setEditingQuestion({ ...question });
        setCustomSection('');
        setShowAddModal(true);
        setError('');
    };

    // Filtered sections
    const displaySections = filterSection === 'all'
        ? Object.keys(sections)
        : Object.keys(sections).filter(s => s === filterSection);

    const totalCount = questions.length;
    const activeCount = questions.filter(q => q.is_active !== 0).length;

    if (loading) {
        return <div className="aqm-loading"><div className="aqm-spinner" />Loading questions...</div>;
    }

    return (
        <div className="aqm-container">
            {/* Header */}
            <div className="aqm-header">
                <div>
                    <h2 className="aqm-title">Application Questions</h2>
                    <p className="aqm-subtitle">{totalCount} total &middot; {activeCount} active &middot; {Object.keys(sections).length} sections</p>
                </div>
                <button className="aqm-add-btn" onClick={openAdd}>+ Add Question</button>
            </div>

            {/* Alerts */}
            {error && (
                <div className="aqm-alert aqm-alert-error">
                    {error}
                    <button onClick={() => setError('')}>x</button>
                </div>
            )}
            {success && (
                <div className="aqm-alert aqm-alert-success">
                    {success}
                    <button onClick={() => setSuccess('')}>x</button>
                </div>
            )}

            {/* Section filter pills */}
            <div className="aqm-filter-bar">
                <button className={`aqm-pill ${filterSection === 'all' ? 'active' : ''}`} onClick={() => setFilterSection('all')}>
                    All Sections
                </button>
                {Object.keys(sections).map(s => (
                    <button key={s} className={`aqm-pill ${filterSection === s ? 'active' : ''}`} onClick={() => setFilterSection(s)}>
                        {s} ({sections[s].length})
                    </button>
                ))}
            </div>

            {/* Sections & cards */}
            <div className="aqm-sections">
                {displaySections.length === 0 && (
                    <div className="aqm-empty">No questions found. Click &quot;+ Add Question&quot; to create one.</div>
                )}
                {displaySections.map(sectionName => (
                    <div key={sectionName} className="aqm-section">
                        <h3 className="aqm-section-title">{sectionName}</h3>
                        <div className="aqm-grid">
                            {sections[sectionName].map(question => (
                                <div key={question.id} className={`aqm-card ${question.is_active === 0 ? 'inactive' : ''}`}>
                                    <div className="aqm-card-top">
                                        <span className="aqm-card-text">{question.question_text}</span>
                                        <span className={`aqm-type-badge ${question.field_type}`}>{question.field_type}</span>
                                    </div>
                                    <div className="aqm-card-meta">
                                        <span className="aqm-field-name">{question.field_name}</span>
                                        <div className="aqm-badges">
                                            {question.required ? <span className="aqm-badge required">Required</span> : null}
                                            {question.is_active === 0 && <span className="aqm-badge disabled">Disabled</span>}
                                        </div>
                                    </div>
                                    <div className="aqm-card-actions">
                                        <button className="aqm-btn-edit" onClick={() => openEdit(question)}>Edit</button>
                                        <button
                                            className={`aqm-btn-toggle ${question.is_active === 0 ? 'activate' : ''}`}
                                            onClick={() => handleToggleActive(question)}
                                        >
                                            {question.is_active === 0 ? 'Enable' : 'Disable'}
                                        </button>
                                        <button className="aqm-btn-delete" onClick={() => handleDeleteQuestion(question.id)}>Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Add / Edit Modal */}
            {showAddModal && editingQuestion && (
                <div className="aqm-modal-overlay" onClick={() => { setShowAddModal(false); setEditingQuestion(null); }}>
                    <div className="aqm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="aqm-modal-header">
                            <h3>{editingQuestion.id ? 'Edit Question' : 'Add New Question'}</h3>
                            <button className="aqm-modal-close" onClick={() => { setShowAddModal(false); setEditingQuestion(null); }}>x</button>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveQuestion(editingQuestion); }}>
                            <div className="aqm-modal-body">
                                {/* Section dropdown */}
                                <div className="aqm-field">
                                    <label>Section</label>
                                    <select
                                        value={customSection ? '__custom__' : editingQuestion.section}
                                        onChange={(e) => {
                                            if (e.target.value === '__custom__') {
                                                setCustomSection('');
                                            } else {
                                                setCustomSection('');
                                                setEditingQuestion({ ...editingQuestion, section: e.target.value });
                                            }
                                        }}
                                    >
                                        {allSections.map(s => <option key={s} value={s}>{s}</option>)}
                                        <option value="__custom__">+ Custom Section...</option>
                                    </select>
                                    {(customSection !== '' || editingQuestion.section === '__custom__') && (
                                        <input
                                            type="text"
                                            className="aqm-custom-section"
                                            placeholder="Enter custom section name"
                                            value={customSection}
                                            onChange={(e) => {
                                                setCustomSection(e.target.value);
                                                setEditingQuestion({ ...editingQuestion, section: e.target.value });
                                            }}
                                            autoFocus
                                        />
                                    )}
                                </div>

                                <div className="aqm-field">
                                    <label>Question Text *</label>
                                    <input
                                        type="text"
                                        value={editingQuestion.question_text}
                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text: e.target.value })}
                                        required
                                        placeholder="e.g. What is your full legal name?"
                                    />
                                </div>

                                <div className="aqm-field">
                                    <label>Field Name (unique identifier) *</label>
                                    <input
                                        type="text"
                                        value={editingQuestion.field_name}
                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, field_name: e.target.value })}
                                        required
                                        placeholder="e.g. full_legal_name"
                                    />
                                </div>

                                <div className="aqm-field-row">
                                    <div className="aqm-field">
                                        <label>Field Type</label>
                                        <select
                                            value={editingQuestion.field_type}
                                            onChange={(e) => setEditingQuestion({ ...editingQuestion, field_type: e.target.value })}
                                        >
                                            {FIELD_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                                        </select>
                                    </div>
                                    <div className="aqm-field">
                                        <label>Order Index</label>
                                        <input
                                            type="number"
                                            value={editingQuestion.order_index || 0}
                                            onChange={(e) => setEditingQuestion({ ...editingQuestion, order_index: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>
                                </div>

                                <div className="aqm-field">
                                    <label>Placeholder</label>
                                    <input
                                        type="text"
                                        value={editingQuestion.placeholder || ''}
                                        onChange={(e) => setEditingQuestion({ ...editingQuestion, placeholder: e.target.value })}
                                        placeholder="Placeholder text shown in the input"
                                    />
                                </div>

                                {editingQuestion.field_type === 'select' && (
                                    <div className="aqm-field">
                                        <label>Options (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={editingQuestion.options ? (Array.isArray(editingQuestion.options) ? editingQuestion.options.join(', ') : editingQuestion.options) : ''}
                                            onChange={(e) => setEditingQuestion({ ...editingQuestion, options: e.target.value })}
                                            placeholder="Option 1, Option 2, Option 3"
                                        />
                                    </div>
                                )}

                                <div className="aqm-toggles">
                                    <label className="aqm-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={editingQuestion.required === 1 || editingQuestion.required === true}
                                            onChange={(e) => setEditingQuestion({ ...editingQuestion, required: e.target.checked ? 1 : 0 })}
                                        />
                                        <span>Required</span>
                                    </label>
                                    <label className="aqm-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={editingQuestion.is_active !== 0}
                                            onChange={(e) => setEditingQuestion({ ...editingQuestion, is_active: e.target.checked ? 1 : 0 })}
                                        />
                                        <span>Active</span>
                                    </label>
                                </div>
                            </div>
                            <div className="aqm-modal-footer">
                                <button type="button" className="aqm-btn-cancel" onClick={() => { setShowAddModal(false); setEditingQuestion(null); }}>Cancel</button>
                                <button type="submit" className="aqm-btn-save">{editingQuestion.id ? 'Update Question' : 'Add Question'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicationQuestionsManager;
